import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import EstadoConsultaBadge from '../components/consultas/EstadoConsultaBadge'
import { cerrarConsulta, obtenerConsultas, responderConsulta } from '../services/consultasService'
import { RESPUESTA_MAX, categoriaLabel, formatConsultaDate } from '../utils/consultas'

const filters = [['todas', 'Todas'], ['pendiente', 'Pendientes'], ['respondida', 'Respondidas'], ['cerrada', 'Cerradas']]

function socioNombre(consulta) {
  const socio = consulta.cp_socios
  if (!socio) return 'Socio'
  const nombre = [socio.nombres, socio.apellido_paterno, socio.apellido_materno].filter(Boolean).join(' ')
  return socio.numero_socio ? nombre + ' (N° ' + socio.numero_socio + ')' : nombre
}

function ResponderModal({ consulta, organizacionId, onClose, onSaved }) {
  const [texto, setTexto] = useState(consulta.respuesta || '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const valid = texto.trim().length > 0 && texto.length <= RESPUESTA_MAX

  async function submit(event) {
    event.preventDefault()
    if (!valid || saving) return
    setSaving(true)
    setError('')
    try {
      onSaved(await responderConsulta(organizacionId, consulta.id, texto.trim()))
    } catch (e) {
      setError(e.message)
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-label="Responder consulta">
      <form onSubmit={submit} className="max-h-[92dvh] w-full max-w-lg space-y-4 overflow-y-auto rounded-t-3xl bg-white p-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] shadow-xl sm:rounded-3xl">
        <h2 className="text-lg font-bold">Responder consulta</h2>
        <div className="rounded-xl bg-background p-4 text-sm"><p className="font-semibold">{socioNombre(consulta)}</p><p className="mt-1 text-text-secondary">{categoriaLabel(consulta.categoria)} · {formatConsultaDate(consulta.created_at)}</p><p className="mt-2 whitespace-pre-wrap break-words">{consulta.consulta}</p></div>
        <div>
          <label htmlFor="respuesta" className="block text-sm font-semibold">Respuesta</label>
          <textarea id="respuesta" rows={5} maxLength={RESPUESTA_MAX} value={texto} onChange={(event) => setTexto(event.target.value)} className="mt-2 block w-full resize-y rounded-xl border border-border px-4 py-3 text-base outline-none focus:border-secondary focus:ring-4 focus:ring-secondary/15" />
          <p className="mt-1.5 text-right text-sm font-semibold text-text-secondary">{texto.length} / {RESPUESTA_MAX}</p>
        </div>
        {error && <p className="text-sm text-danger" role="alert">{error}</p>}
        <div className="flex flex-col gap-2 sm:flex-row-reverse">
          <button type="submit" disabled={!valid || saving} className="min-h-12 rounded-xl bg-primary px-5 py-3 font-semibold text-white disabled:opacity-60">{saving ? 'Guardando...' : 'Guardar respuesta'}</button>
          <button type="button" onClick={onClose} disabled={saving} className="min-h-12 rounded-xl border border-border px-5 py-3 font-semibold text-primary">Cancelar</button>
        </div>
      </form>
    </div>
  )
}

export default function ConsultasPage() {
  const { organizacion } = useAuth()
  const [estado, setEstado] = useState('todas')
  const [consultas, setConsultas] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [answering, setAnswering] = useState(null)
  const [closingId, setClosingId] = useState('')

  useEffect(() => {
    let active = true
    setLoading(true)
    setError('')
    obtenerConsultas(organizacion.id, estado)
      .then((data) => { if (active) setConsultas(data) })
      .catch((e) => { if (active) setError(e.message) })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [organizacion.id, estado])

  function replace(updated) {
    setConsultas((current) => current.map((item) => item.id === updated.id ? updated : item))
  }

  async function close(consulta) {
    if (!window.confirm('¿Deseas cerrar esta consulta?')) return
    setClosingId(consulta.id)
    setError('')
    setMessage('')
    try {
      replace(await cerrarConsulta(organizacion.id, consulta.id))
      setMessage('Consulta cerrada.')
    } catch (e) {
      setError(e.message)
    } finally {
      setClosingId('')
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-5">
      <h2 className="text-2xl font-bold tracking-tight">Consultas de socios</h2>
      <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar por estado">
        {filters.map(([value, label]) => <button key={value} type="button" onClick={() => setEstado(value)} aria-pressed={estado === value} className={'min-h-11 rounded-xl border px-4 text-sm font-semibold ' + (estado === value ? 'border-primary bg-primary text-white' : 'border-border bg-white text-primary')}>{label}</button>)}
      </div>
      {message && <div className="rounded-xl border border-success/30 bg-success/10 p-4 text-sm font-medium text-success-text" role="status">{message}</div>}
      {error && <div className="rounded-xl border border-danger/25 bg-danger/5 p-4 text-sm text-danger" role="alert">{error}</div>}
      {loading ? <p className="py-12 text-center text-sm text-text-secondary" role="status">Cargando consultas…</p>
        : consultas.length === 0 ? <p className="rounded-2xl border border-border bg-surface p-8 text-center text-text-secondary">No hay consultas para mostrar.</p>
        : <div className="space-y-3">{consultas.map((consulta) => (
          <article key={consulta.id} className="rounded-2xl border border-border bg-surface p-5 shadow-sm">
            <div className="flex flex-wrap items-start justify-between gap-2"><div className="min-w-0"><p className="break-words font-semibold">{socioNombre(consulta)}</p><p className="text-sm text-text-secondary">{categoriaLabel(consulta.categoria)} · {formatConsultaDate(consulta.created_at)}</p></div><EstadoConsultaBadge estado={consulta.estado} /></div>
            <p className="mt-3 whitespace-pre-wrap break-words">{consulta.consulta}</p>
            {consulta.respuesta && <div className="mt-3 rounded-xl bg-secondary/10 p-4 text-sm"><p className="text-xs font-bold uppercase tracking-wide text-primary">Respuesta</p><p className="mt-1 whitespace-pre-wrap break-words">{consulta.respuesta}</p></div>}
            {consulta.estado !== 'cerrada' && <div className="mt-4 flex flex-col gap-2 sm:flex-row">
              {consulta.estado === 'pendiente' && <button type="button" onClick={() => setAnswering(consulta)} className="min-h-11 rounded-xl bg-primary px-4 py-2.5 font-semibold text-white">Responder</button>}
              <button type="button" onClick={() => close(consulta)} disabled={closingId === consulta.id} className="min-h-11 rounded-xl border border-border px-4 py-2.5 font-semibold text-primary disabled:opacity-50">{closingId === consulta.id ? 'Cerrando…' : 'Cerrar consulta'}</button>
            </div>}
          </article>
        ))}</div>}
      {answering && <ResponderModal consulta={answering} organizacionId={organizacion.id} onClose={() => setAnswering(null)} onSaved={(updated) => { replace(updated); setAnswering(null); setMessage('Respuesta guardada.') }} />}
    </div>
  )
}
